import { Component } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Countries } from 'src/app/enums/countries';
import { UserProfile } from 'src/app/interfaces/UserProfile';
import { AvailableRole } from 'src/app/interfaces/available-role';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { GeneralService } from 'src/app/services/general.service';
import { TranslatorService } from 'src/app/services/translator.service';
import { UserManagementService } from 'src/app/services/user-management.service';

@Component({
    selector: 'app-edit-user',
    templateUrl: './edit-user.component.html',
    styleUrls: ['./edit-user.component.scss'],
    standalone: false
})
export class EditUserComponent {
    selectedLanguage: string = 'EN';
    isLoading = true;
    isSaving = false;
    isSending = false;
    authenticatedUserProfile: UserProfile | undefined;
    editUserProfile: UserProfile = {
        userData: {
            isApproved: false, userName: '', middleName: '', nameSuffix: '', numberOfTimesLoggedIn: 0,
            address: { addressLine1: '', addressLine2: '', city: '', country: '', countryId: Countries.NA, state: '', zip: '' }, primaryPhoneNumber: '', secondaryPhoneNumber: '',
            roles: [], lastActivityDate: '', lastLoginDate: '', creationDate: '', isAdmin: false, cdbCustomerId: -1,
            lastName: '', email: '', firstName: '', userType: '',  faxNumber: '', loginType: '', statusCode: '',
        },
        customerId: '',
        customerType: 0,
        firstName: '',
        lastName: '',
        numberOfTimesLoggedIn: 0,
        customer: {},
        primaryContactEmail: ''
    }
    customerId: string = '';
    userIsActive: boolean = false;
    customerIsActive: boolean = false;

    roles: AvailableRole[] = [];
    rolesUpdated:boolean = false;
    roleFriendlyNames: any =
        {
            "roles": [
                {
                    "roleName": "mc_view",
                    "translations": [
                        { "language": "EN", "friendlyName": "View Records" },
                        { "language": "ES", "friendlyName": "Ver Registros" },
                        { "language": "FR", "friendlyName": "Voir les Dossiers" }
                    ]
                },
                {
                    "roleName": "mc_request",
                    "translations": [
                        { "language": "EN", "friendlyName": "Request Records" },
                        { "language": "ES", "friendlyName": "Solicitar Registros" },
                        { "language": "FR", "friendlyName": "Demander des Dossiers" }
                    ]
                },
                {
                    "roleName": "mc_useradmin",
                    "translations": [
                        { "language": "EN", "friendlyName": "Account Administrator" },
                        { "language": "ES", "friendlyName": "Administrador de Cuentas" },
                        { "language": "FR", "friendlyName": "Administrateur du compte" }
                    ]
                }
            ]
        };

    rolesAreValid: boolean = true;
    rolesDirty:boolean = false;
    isPowerUser: boolean = false;

    toastTitle: string = "";
    toastTxt: string = "";
    showToast: number = 0;
    toastIsError: boolean = false;
    userProfileTestString: string = "";
    editUserGroup!: FormGroup;


    constructor(public generalService: GeneralService, public translator: TranslatorService,
        private authenticationService: AuthenticationService, private router: Router,
        private userManagementService: UserManagementService) {
        this.authenticatedUserProfile = this.authenticationService.getUserProfile() as UserProfile;

        this.customerId = this.authenticatedUserProfile!.customerId;

        this.loadUserProfile();
        this.isLoading = true;

    }

    ngOnInit(): void {
        this.editUserGroup = new FormGroup({
            userName: new FormControl({ value: this.editUserProfile.userData.userName, disabled: true }, [
                Validators.required,
                Validators.maxLength(50),
                Validators.pattern(/^([a-zA-Z0-9]*)$/),

            ]),
            firstName: new FormControl({value: this.editUserProfile.userData.firstName, disabled: true }, [
                Validators.required,
                Validators.maxLength(50),
                Validators.pattern(/^([a-zA-Z ]*)$/)
            ]),
            lastName: new FormControl({value: this.editUserProfile.userData.lastName, disabled: true }, [
                Validators.required,
                Validators.maxLength(50),
                Validators.pattern(/^([a-zA-Z ]*)$/)
            ]),
            email: new FormControl({ value: this.editUserProfile.userData.email, disabled: true }, [
                Validators.required,
                Validators.maxLength(50),
                Validators.pattern(/^[-a-z0-9'!_-}{\'?]+(\.[-a-z0-9'!_-}{\'?]+)*@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9_-]+\.)+[a-zA-Z]{2,}))$/i)
            ])
        });
        this.scrollToTop();
    }

    updateValadator() {
        this.editUserGroup.get('userName')?.setValue(this.editUserProfile.userData.userName);
        this.editUserGroup.get('firstName')?.setValue(this.editUserProfile.userData.firstName);
        this.editUserGroup.get('lastName')?.setValue(this.editUserProfile.userData.lastName);
        this.editUserGroup.get('email')?.setValue(this.editUserProfile.userData.email);
    }

    get username() { return this.editUserGroup!.get('userName') };
    get firstName() { return this.editUserGroup!.get('firstName') };
    get lastName() { return this.editUserGroup!.get('lastName') };
    get email() { return this.editUserGroup!.get('email') };

    loadUserProfile() {
        const queryString = window.location.href;
        
        const prams = this.generalService.getQueryParameters(queryString);
        const username = prams['id'];
        if (username === '') {
            this.router.navigate(['/accessDenied']);
        }

        const profilePromise = this.userManagementService.getUserProfile(username!);
        profilePromise.subscribe((response: UserProfile | any) => {
            if (response) {
                this.userProfileTestString = "";
                this.editUserProfile = response;
                this.updateValadator();
                this.userIsActive = response.userData.isApproved;
                this.customerIsActive = this.authenticatedUserProfile!.customer.cdbCustomerStatus === 'A';
                this.isPowerUser = this.authenticatedUserProfile!.customer!.powerUserEmail === this.editUserProfile.userData.email;
                this.getRoles();
                this.userProfileTestString = JSON.stringify(this.editUserProfile);
                this.isLoading = false;
            } else {
                console.error('User profile not found for username: ' + username);
                this.isLoading = false;
                this.router.navigate(['/accessDenied']);
            }
        })
    }

    hasErrors() {
        if(this.editUserGroup.valid && this.rolesAreValid){
            return null;
        }else if(this.editUserGroup.dirty && this.editUserGroup.valid){
            return null;
        }
        return 'true';
    }

    

    updateUserRoles(role: AvailableRole) {
        if (role.roleName && this.editUserProfile && this.editUserProfile.userData.roles) {
            const adding = role.isSelected;
            if (adding) {
                const roleIndex = this.editUserProfile.userData.roles.findIndex(r => r === role.roleName);
                const alreadyHasRole = roleIndex > -1;
                if (alreadyHasRole === false) {
                    this.editUserProfile.userData.roles.push(role.roleName);
                }
            } else {
                this.editUserProfile!.userData.roles = this.editUserProfile!.userData!.roles.filter(r => r !== role.roleName);
            }
            
        }
        this.rolesDirty = true;
        this.rolesAreValid = this.testRolesValidity();
    }

    disableRoleEdit(role: AvailableRole): boolean {
        return true;
        // if (this.isPowerUser && role.roleName === 'mc_useradmin') {
        //     return true;
        // } else {
        //     return false;
        // }
    }

    getRoles(isTranslation: boolean = false) {
        this.roles = [];
        this.isLoading = true;
        const rolesPromise = this.userManagementService.getAvailableRoles();
        rolesPromise.subscribe((response: [] | any) => {
            if (response && response.length > 0) {
                this.roles = [];
                response.forEach((role: string) => {
                    const availableRole: AvailableRole = {
                        roleName: role,
                        friendlyName: '',
                        isSelected: this.editUserProfile!.userData!.roles.includes(role)
                    }
                    this.roles.push(availableRole);
                })
            }
            this.rolesAreValid = this.testRolesValidity();
            if (!isTranslation) {
                this.isLoading = false;
            }
        })
    }

    testRolesValidity() {
        let rolesValid = false;
        const selectedRoles = this.roles.filter(role => role.isSelected === true);
        if (selectedRoles.length > 0) {
            rolesValid = true;
        }
        return rolesValid;
    }


    scrollToTop() {
        window.scrollTo(0, 0);
    }
}
